import { ApiError } from "@agentry/api-client";
import type { WorkOrderSessionRole } from "@agentry/zerovia-contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../../app/ApiProvider";
import { useScope } from "../../../app/AuthProvider";
import { useAction, useAsync } from "../../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	cx,
	DefinitionList,
	PageHeader,
	TextArea,
} from "../../../ui/primitives";
import { ErrorState, LoadingState, NotFoundState } from "../../../ui/states";
import {
	Conversation,
	formatDateTime,
	journeyResultLabel,
	journeyScriptLabel,
	ProgressSummary,
	WorkOrderStatus,
} from "../board/components";
import { renderMarkdown } from "../markdown";

const ROLES: Array<{ value: WorkOrderSessionRole; label: string }> = [
	{ value: "design", label: "設計" },
	{ value: "implement", label: "実装" },
	{ value: "accept", label: "受け入れ" },
];

export function WorkOrderDetailPage() {
	const { orderId } = useParams();
	const { client } = useApi();
	const scope = useScope();
	const detail = useAsync(async () => {
		if (!orderId) throw new Error("orderId is required");
		return client.getWorkOrderDetail(scope, orderId);
	}, [client, scope.tenantId, scope.projectId, orderId]);

	if (!orderId) return <NotFoundState />;
	if (detail.status === "loading") return <LoadingState label="カード詳細を読み込んでいます" />;
	if (detail.status === "error") return <ErrorState error={detail.error} onRetry={detail.reload} />;

	return <DetailContent data={detail.data} reload={detail.reload} />;
}

function DetailContent({
	data,
	reload,
}: {
	data: Awaited<ReturnType<ReturnType<typeof useApi>["client"]["getWorkOrderDetail"]>>;
	reload(): void;
}) {
	const { client } = useApi();
	const scope = useScope();
	const { order } = data;
	const [reason, setReason] = useState("");
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const deleteButtonRef = useRef<HTMLButtonElement>(null);
	const deleteInFlight = useRef(false);
	const transition = useAction(async (to: "issued" | "accepted" | "rejected") =>
		client.transitionWorkOrder(scope, order.id, to, reason.trim() || undefined),
	);
	const restore = useAction(async () => client.restoreWorkOrder(scope, order.id));
	const remove = useAction(async () => client.deleteWorkOrder(scope, order.id));
	const transitionError =
		transition.error instanceof ApiError
			? `${transition.error.code}: ${transition.error.message}`
			: null;

	useEffect(() => {
		if (client.mode !== "real") return;
		const subscriptions = data.sessions
			.filter((session) => session.framesAvailable && session.status === "active")
			.map((session) => {
				const lastSequence = Math.max(
					-1,
					...data.frames
						.filter((frame) => frame.sessionId === session.id)
						.map((frame) => frame.sequence),
				);
				return client.subscribeWorkOrderFrames(scope, order.id, session.id, {
					lastSequence,
					onEvent: reload,
					onGap: reload,
					onError: () => undefined,
				});
			});
		if (data.progress?.live) {
			subscriptions.push(
				client.subscribeWorkOrderProgress(scope, order.id, {
					lastSequence: data.progress.sequence,
					onEvent: reload,
					onGap: reload,
					onError: () => undefined,
				}),
			);
		}
		return () => {
			for (const subscription of subscriptions) subscription.close();
		};
	}, [client, scope, order.id, data, reload]);

	async function changeStatus(to: "issued" | "accepted" | "rejected") {
		const result = await transition.run(to);
		if (result) reload();
	}

	async function restoreCard() {
		const result = await restore.run();
		if (result) reload();
	}

	async function deleteCard() {
		if (deleteInFlight.current || data.progress?.live) return;
		deleteInFlight.current = true;
		const result = await remove.run();
		deleteInFlight.current = false;
		if (!result) return;
		setDeleteDialogOpen(false);
		reload();
	}

	return (
		<>
			{deleteDialogOpen && (
				<DeleteConfirmationDialog
					title={order.title}
					pending={remove.pending}
					error={remove.error}
					onCancel={() => setDeleteDialogOpen(false)}
					onConfirm={deleteCard}
					restoreFocusRef={deleteButtonRef}
				/>
			)}
			<Link
				to="/app/board"
				className="mb-3 inline-flex text-[13px] font-semibold text-brand-700 hover:underline"
			>
				← 発注ボードへ
			</Link>
			<PageHeader
				eyebrow={order.id}
				title={order.title}
				description={`${order.targetRepo} / ${order.branch}`}
				actions={<WorkOrderStatus status={order.status} />}
			/>

			<div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
				<div className="space-y-5">
					{order.kind === "journey" && <JourneyResultCard evidence={data.evidence} />}
					{order.deletedAt && (
						<Callout tone="warning" title="このカードは削除済みです">
							<p className="mb-2">削除日時: {formatDateTime(order.deletedAt)}</p>
							<Button size="sm" onClick={restoreCard} disabled={restore.pending}>
								{restore.pending ? "復元中…" : "カードを復元"}
							</Button>
							{Boolean(restore.error) && (
								<p role="alert" className="mt-2 text-[12px] text-danger-600">
									カードを復元できませんでした。
								</p>
							)}
						</Callout>
					)}

					{data.progress && (
						<Card title="工程ライブ" description="現在工程、再試行理由、開始時刻を表示します。">
							<ProgressSummary progress={data.progress} />
						</Card>
					)}

					<Card title="発注仕様">
						<div
							className="markdown-body"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: renderMarkdownはescape後に限定タグのみ生成する
							dangerouslySetInnerHTML={{ __html: renderMarkdown(order.specMarkdown) }}
						/>
					</Card>

					<QuestionSection data={data} reload={reload} />
					<SessionChat data={data} reload={reload} />

					<div className="grid gap-5 lg:grid-cols-2">
						<Card title="記録" description="詳細確認・受け入れ・備考">
							{data.records.length === 0 ? (
								<p className="text-[13px] text-ink-500">まだ記録はありません。</p>
							) : (
								<ul className="space-y-3">
									{data.records.map((record) => (
										<li
											key={record.id}
											className="border-b border-ink-100 pb-3 last:border-0 last:pb-0"
										>
											<div className="flex items-center justify-between gap-2">
												<Badge tone={record.kind === "受け入れ" ? "success" : "neutral"}>
													{record.kind}
												</Badge>
												<span className="text-[11px] text-ink-400">
													{formatDateTime(record.createdAt)}
												</span>
											</div>
											<p className="mt-1.5 text-[13px] leading-relaxed text-ink-700">
												{record.bodyMarkdown}
											</p>
										</li>
									))}
								</ul>
							)}
						</Card>

						<Card title="Evidence" description="CI・release・検証記録">
							{data.evidence.length === 0 ? (
								<p className="text-[13px] text-ink-500">まだ Evidence はありません。</p>
							) : (
								<ul className="space-y-3">
									{data.evidence.map((item) => {
										const url = typeof item.payload.url === "string" ? item.payload.url : null;
										return (
											<li key={item.id} className="rounded-lg bg-ink-50 p-3">
												<div className="flex items-center justify-between gap-2">
													<Badge tone="brand">{item.kind}</Badge>
													<span className="text-[11px] text-ink-400">
														{formatDateTime(item.createdAt)}
													</span>
												</div>
												{url?.startsWith("https://") && (
													<a
														href={url}
														target="_blank"
														rel="noreferrer"
														className="mt-2 block break-all text-[12px] font-semibold text-brand-700 hover:underline"
													>
														記録を開く
													</a>
												)}
											</li>
										);
									})}
								</ul>
							)}
						</Card>
					</div>
				</div>

				<aside className="space-y-5">
					<Card title="カード情報">
						<DefinitionList
							columns={1}
							items={[
								{ label: "Repository", value: order.targetRepo },
								{ label: "Branch", value: order.branch, mono: true },
								{ label: "Created", value: formatDateTime(order.createdAt) },
								{ label: "Updated", value: formatDateTime(order.updatedAt) },
							]}
						/>
					</Card>

					{client.mode === "real" && !order.deletedAt && (
						<Card title="カードの削除" description="不要になったカードを論理削除します。">
							<Button
								ref={deleteButtonRef}
								size="sm"
								variant="danger"
								disabled={Boolean(data.progress?.live) || remove.pending}
								onClick={() => setDeleteDialogOpen(true)}
							>
								カードを削除
							</Button>
							{data.progress?.live && (
								<p className="mt-2 text-[12px] text-danger-600">実行中のカードは削除できません</p>
							)}
						</Card>
					)}

					{!order.deletedAt && (order.status === "draft" || order.status === "verified") && (
						<Card title={order.status === "draft" ? "発注を承認" : "受け入れ裁定"}>
							<label htmlFor="transition-reason" className="ag-label">
								判断メモ（任意）
							</label>
							<TextArea
								id="transition-reason"
								rows={3}
								value={reason}
								onChange={(event) => setReason(event.target.value)}
								placeholder="判断理由を記録できます"
							/>
							<div className="mt-3 flex flex-wrap gap-2">
								{order.status === "draft" ? (
									<Button
										size="sm"
										onClick={() => changeStatus("issued")}
										disabled={transition.pending}
									>
										発注する
									</Button>
								) : (
									<>
										<Button
											size="sm"
											onClick={() => changeStatus("accepted")}
											disabled={transition.pending}
										>
											受け入れる
										</Button>
										<Button
											size="sm"
											variant="danger"
											onClick={() => changeStatus("rejected")}
											disabled={transition.pending}
										>
											差し戻す
										</Button>
									</>
								)}
							</div>
							{Boolean(transition.error) && (
								<p role="alert" className="mt-2 text-[12px] text-danger-600">
									状態を更新できませんでした。
									{transitionError && (
										<span className="mt-1 block font-mono">{transitionError}</span>
									)}
								</p>
							)}
						</Card>
					)}
				</aside>
			</div>
		</>
	);
}

function JourneyResultCard({
	evidence,
}: {
	evidence: Array<{ kind: string; payload: Record<string, unknown> }>;
}) {
	const latest = [...evidence].reverse().find((item) => item.kind === "journey_result")?.payload;
	if (!latest) {
		return (
			<Card title="ジャーニー結果">
				<p className="text-[13px] text-ink-500">予約または実行待ちです。</p>
			</Card>
		);
	}
	const screenshots = Array.isArray(latest.screenshots)
		? latest.screenshots.filter(
				(item): item is { name: string; mimeType: string; dataBase64: string } =>
					Boolean(
						item &&
							typeof item === "object" &&
							typeof item.name === "string" &&
							typeof item.mimeType === "string" &&
							typeof item.dataBase64 === "string",
					),
			)
		: [];
	const result = typeof latest.result === "string" ? latest.result : "scheduled";
	const script = typeof latest.script === "string" ? latest.script : "journey";
	return (
		<Card
			title="ジャーニー結果"
			action={
				<Badge tone={result === "passed" ? "success" : result === "failed" ? "danger" : "warning"}>
					{journeyResultLabel(result)}
				</Badge>
			}
		>
			<p className="text-[13px] font-semibold text-ink-800">{journeyScriptLabel(script)}</p>
			{typeof latest.durationSeconds === "number" && (
				<p className="mt-1 text-[12px] text-ink-500">
					壁時計 {latest.durationSeconds.toFixed(1)} 秒
				</p>
			)}
			{typeof latest.failedStep === "string" && latest.failedStep && (
				<Callout tone="danger" title="失敗手順">
					{latest.failedStep}
				</Callout>
			)}
			{typeof latest.evaluationScore === "number" && (
				<p className="mt-2 text-[12.5px] text-ink-700">
					評価v2 {latest.evaluationScore}% / Fail {String(latest.evaluationFailCount ?? 0)}件
				</p>
			)}
			{screenshots.length > 0 && (
				<section
					className="mt-3 grid gap-3 sm:grid-cols-2"
					aria-label="スクリーンショットギャラリー"
				>
					{screenshots.map((item) => (
						<figure
							key={item.name}
							className="overflow-hidden rounded-lg border border-ink-200 bg-white"
						>
							<img
								src={`data:${item.mimeType};base64,${item.dataBase64}`}
								alt={item.name}
								className="h-auto w-full"
							/>
							<figcaption className="truncate px-2 py-1 text-[10.5px] text-ink-500">
								{item.name}
							</figcaption>
						</figure>
					))}
				</section>
			)}
		</Card>
	);
}

function DeleteConfirmationDialog({
	title,
	pending,
	error,
	onCancel,
	onConfirm,
	restoreFocusRef,
}: {
	title: string;
	pending: boolean;
	error: unknown;
	onCancel(): void;
	onConfirm(): void;
	restoreFocusRef: React.RefObject<HTMLButtonElement | null>;
}) {
	const cancelRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		cancelRef.current?.focus();
		return () => restoreFocusRef.current?.focus();
	}, [restoreFocusRef]);

	return (
		<div
			className="fixed inset-0 z-50 grid place-items-center bg-ink-950/50 p-4"
			role="presentation"
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="delete-card-title"
				aria-describedby="delete-card-description"
				className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
				onKeyDown={(event) => {
					if (event.key === "Escape" && !pending) onCancel();
				}}
			>
				<h2 id="delete-card-title" className="text-lg font-bold text-ink-900">
					カードを削除
				</h2>
				<p id="delete-card-description" className="mt-3 text-sm leading-relaxed text-ink-700">
					「{title}」を論理削除します。削除済み一覧から後で復元できます。
				</p>
				{Boolean(error) && (
					<p role="alert" className="mt-3 text-sm font-semibold text-danger-600">
						カードを削除できませんでした。
					</p>
				)}
				<div className="mt-5 flex justify-end gap-2">
					<Button
						ref={cancelRef}
						variant="secondary"
						size="sm"
						disabled={pending}
						onClick={onCancel}
					>
						キャンセル
					</Button>
					<Button variant="danger" size="sm" disabled={pending} onClick={onConfirm}>
						{pending ? "削除中…" : "削除を確定"}
					</Button>
				</div>
			</div>
		</div>
	);
}

function QuestionSection({
	data,
	reload,
}: {
	data: Parameters<typeof DetailContent>[0]["data"];
	reload(): void;
}) {
	const open = data.questions.find((question) => question.status === "open");
	const { client } = useApi();
	const scope = useScope();
	const [answer, setAnswer] = useState("");
	const action = useAction(async () => {
		if (!open) throw new Error("open question is required");
		return client.answerWorkOrderQuestion(scope, data.order.id, open.id, answer);
	});
	if (!open) return null;

	async function submit(event: React.FormEvent) {
		event.preventDefault();
		const result = await action.run();
		if (result) reload();
	}

	return (
		<Card title="裁定待ち" description="回答すると元の工程へ戻って作業を再開します。">
			<Callout tone="warning" title="実装担当からの質問">
				{open.questionMarkdown}
			</Callout>
			<form onSubmit={submit} className="mt-3 space-y-2">
				<label htmlFor="question-answer" className="ag-label">
					回答
				</label>
				<TextArea
					id="question-answer"
					rows={3}
					value={answer}
					onChange={(event) => setAnswer(event.target.value)}
				/>
				<Button type="submit" size="sm" disabled={!answer.trim() || action.pending}>
					{action.pending ? "回答中…" : "回答して再開"}
				</Button>
			</form>
		</Card>
	);
}

function SessionChat({
	data,
	reload,
}: {
	data: Parameters<typeof DetailContent>[0]["data"];
	reload(): void;
}) {
	const { client } = useApi();
	const scope = useScope();
	const [role, setRole] = useState<WorkOrderSessionRole>("implement");
	const [message, setMessage] = useState("");
	const sessionIds = useMemo(
		() =>
			new Set(
				data.sessions.filter((session) => session.role === role).map((session) => session.id),
			),
		[data.sessions, role],
	);
	const frames = data.frames.filter((frame) => sessionIds.has(frame.sessionId));
	const send = useAction(async () =>
		client.sendWorkOrderSessionMessage(scope, data.order.id, role, message),
	);

	async function submit(event: React.FormEvent) {
		event.preventDefault();
		const result = await send.run();
		if (!result) return;
		setMessage("");
		reload();
	}

	return (
		<Card
			title="カード内チャット"
			description="設計・実装・受け入れ各セッションの frames を分けて表示します。"
			action={
				<Badge tone={client.mode === "real" ? "success" : "neutral"}>
					{client.mode === "real" ? "Real frames / SSE" : "Mock"}
				</Badge>
			}
		>
			<div
				className="mb-3 flex gap-1 rounded-xl bg-ink-100 p-1"
				role="tablist"
				aria-label="担当セッション"
			>
				{ROLES.map((item) => (
					<button
						key={item.value}
						type="button"
						role="tab"
						aria-selected={role === item.value}
						onClick={() => setRole(item.value)}
						className={cx(
							"min-h-9 flex-1 rounded-lg px-2 text-[12.5px] font-bold",
							role === item.value
								? "bg-white text-brand-700 shadow-sm"
								: "text-ink-500 hover:text-ink-800",
						)}
					>
						{item.label}
					</button>
				))}
			</div>
			<Conversation frames={frames} />
			<form onSubmit={submit} className="mt-3 flex items-end gap-2">
				<div className="min-w-0 flex-1">
					<label htmlFor="card-message" className="sr-only">
						担当へメッセージ
					</label>
					<TextArea
						id="card-message"
						rows={2}
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						placeholder={`${ROLES.find((item) => item.value === role)?.label}担当へ確認する`}
					/>
				</div>
				<Button type="submit" size="sm" disabled={!message.trim() || send.pending}>
					送信
				</Button>
			</form>
		</Card>
	);
}
